import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { toggleEventLike } from '../../services/api';

interface LikeButtonProps {
  eventId: string;
  initialLiked: boolean;
  initialCount: number;
  onLikeChange?: (liked: boolean, count: number) => void;
  size?: 'sm' | 'md';
}

export function LikeButton({
  eventId,
  initialLiked,
  initialCount,
  onLikeChange,
  size = 'md',
}: LikeButtonProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [liked, setLiked] = useState(initialLiked);
  const [count, setCount] = useState(initialCount);
  const [isLoading, setIsLoading] = useState(false);

  async function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isLoading) return;

    setIsLoading(true);
    try {
      const result = await toggleEventLike(eventId);
      setLiked(result.liked);
      setCount(result.likeCount);
      onLikeChange?.(result.liked, result.likeCount);
    } catch (error) {
      console.error('Failed to toggle like:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const padding = size === 'sm' ? 'px-2 py-1' : 'px-3 py-1.5';

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`flex items-center gap-1.5 ${padding} rounded-lg transition-all ${
        liked
          ? 'bg-[#e10600]/20 text-[#e10600]'
          : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'
      } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <svg
        className={iconSize}
        fill={liked ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
      <span className={textSize}>{count}</span>
    </button>
  );
}
